import { ChevronDown, Info } from "lucide-react";
import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { extrairTaxaReal } from "../utils/math/rates";
import { ResultadoMarcacao } from "./MarcacaoMercadoInputCard";

const formatBRL = (val: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
        val,
    );

const formatPct = (val: number) =>
    new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(val) + "%";

const renderCardRate = (taxaNominalGerada: number, resultado: ResultadoMarcacao) => {
    if (resultado.isIPCA) {
        const taxaReal = extrairTaxaReal(taxaNominalGerada, resultado.ipcaUtilizado);
        return (
            <>
                <span className="text-xl font-bold text-gray-400 tracking-normal mr-1.5">
                    IPCA +
                </span>
                <span>{formatPct(taxaReal)}</span>
            </>
        );
    }
    return (
        <>
            <span>{formatPct(taxaNominalGerada)}</span>{" "}
            <span className="text-lg font-semibold text-gray-400 tracking-normal">
                a.a.
            </span>
        </>
    );
};

const formatInlineRate = (taxaNominalGerada: number, resultado: ResultadoMarcacao) => {
    if (resultado.isIPCA) {
        const taxaReal = extrairTaxaReal(taxaNominalGerada, resultado.ipcaUtilizado);
        return formatPct(taxaReal);
    }
    return formatPct(taxaNominalGerada);
};

export const MarcacaoMercadoResponse = ({
    resultado,
}: {
    resultado: ResultadoMarcacao;
}) => {
    return (
        <MainCard className="mt-4">
            <MainCardTitle className="flex items-center">
                Rentabilidade Mínima Aceitável
                <div className="relative flex items-center group cursor-help ml-2">
                    <Info
                        size={20}
                        className="text-gray-400 hover:text-calculator-primary transition-colors"
                    />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-72 p-3 bg-gray-800 text-white text-xs rounded-md shadow-lg z-10 font-normal leading-relaxed text-center">
                        Esta é a <strong>TIR Implícita</strong> do negócio. Representa a
                        taxa exata de empate. Se você reinvestir o valor líquido sacado hoje
                        a essa taxa, terá no vencimento os exatos mesmos{" "}
                        {formatBRL(resultado.montanteFicar)} projetados pelo título original.
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                </div>
            </MainCardTitle>

            {/* Cards de rentabilidade mínima */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="p-5 bg-primary rounded border flex flex-col justify-center items-center text-center">
                    <span className="text-sm font-medium mb-1 text-white">
                        Para títulos ISENTOS (Ex: LCI, LCA)
                    </span>
                    <span className="text-3xl font-black text-white flex items-baseline">
                        {renderCardRate(resultado.core.rentabilidadeMinima.taxaIsenta, resultado)}
                    </span>
                </div>
                <div className="p-5 bg-primary rounded border flex flex-col justify-center items-center text-center">
                    <span className="text-sm font-medium mb-1 text-white">
                        Para títulos TRIBUTADOS (Ex: CDB, Tesouro)
                    </span>
                    <span className="text-3xl font-black text-white flex items-baseline">
                        {renderCardRate(resultado.core.rentabilidadeMinima.taxaTributada, resultado)}
                    </span>
                </div>
            </div>

            {/* Projeção da oferta (opcional) */}
            {resultado.simulacaoIsenta && resultado.simulacaoTributada && (
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-6">
                    <h4 className="text-lg font-bold text-calculator-text mb-4 border-b border-gray-100 pb-2">
                        Projeção para a sua oferta de{" "}
                        {formatInlineRate(resultado.simulacaoIsenta.taxaOfertada, resultado)}
                    </h4>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="flex flex-col space-y-1">
                            <span className="text-xs font-bold text-gray-400 uppercase">
                                Se não fizer nada
                            </span>
                            <span className="text-xl font-bold text-gray-600">
                                {formatBRL(resultado.montanteFicar)}
                            </span>
                            <span className="text-sm text-gray-400">Líquido no vencimento</span>
                        </div>

                        <div className="flex flex-col space-y-1 border-l-0 lg:border-l lg:border-gray-100 lg:pl-6">
                            <span className="text-xs font-bold text-gray-400 uppercase">
                                Trocando por um Isento
                            </span>
                            <span className="text-xl font-bold text-calculator-text">
                                {formatBRL(resultado.simulacaoIsenta.valorFinalLiquido)}
                            </span>
                            <span
                                className={`text-sm font-bold ${resultado.simulacaoIsenta.ganhoReal > 0
                                        ? "text-green-700"
                                        : "text-red-700"
                                    }`}
                            >
                                {resultado.simulacaoIsenta.ganhoReal > 0 ? "+" : ""}
                                {formatBRL(resultado.simulacaoIsenta.ganhoReal)}
                            </span>
                        </div>

                        <div className="flex flex-col space-y-1 border-l-0 lg:border-l lg:border-gray-100 lg:pl-6">
                            <span className="text-xs font-bold text-gray-400 uppercase">
                                Trocando por Tributado
                            </span>
                            <span className="text-xl font-bold text-calculator-text">
                                {formatBRL(resultado.simulacaoTributada.valorFinalLiquido)}
                            </span>
                            <span
                                className={`text-sm font-bold ${resultado.simulacaoTributada.ganhoReal > 0
                                        ? "text-green-700"
                                        : "text-red-700"
                                    }`}
                            >
                                {resultado.simulacaoTributada.ganhoReal > 0 ? "+" : ""}
                                {formatBRL(resultado.simulacaoTributada.ganhoReal)}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Detalhes intermediários */}
            <details className="group">
                <summary className="flex items-center justify-between cursor-pointer list-none p-4 rounded-md bg-white border border-gray-200 hover:bg-gray-50 transition-colors font-medium text-calculator-text">
                    <span>Mostrar cálculos intermediários e detalhes do título</span>
                    <span className="transition group-open:rotate-180">
                        <ChevronDown size={20} className="text-gray-400" />
                    </span>
                </summary>

                <div className="p-4 mt-2 bg-white border border-gray-200 rounded-md text-sm text-gray-600 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-gray-100 pb-3">
                        <p>
                            <strong>Prazo Faltante:</strong>{" "}
                            {resultado.core.intermediarios.anosFaltantes.toFixed(2)} anos (
                            {resultado.core.intermediarios.diasFaltantes} dias exatos)
                        </p>
                        <p>
                            <strong>Alíquota IR no Resgate Hoje:</strong>{" "}
                            {resultado.core.intermediarios.aliquotaAtual === 0
                                ? "Isento"
                                : resultado.core.intermediarios.lucroAtualBruto <= 0
                                    ? "Sem lucro tributável"
                                    : `${resultado.core.intermediarios.aliquotaAtual}% sobre o lucro`}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-gray-100 pb-3">
                        <p>
                            <strong>Lucro Bruto Atual:</strong>{" "}
                            {formatBRL(resultado.core.intermediarios.lucroAtualBruto)}
                        </p>
                        <p>
                            <strong>Imposto Retido se Sacar Hoje:</strong>{" "}
                            {formatBRL(resultado.core.intermediarios.impostoRetidoAtual)}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <p className="text-calculator-text font-bold">
                            <strong>Capital para Reinvestir (Líquido Atual):</strong>{" "}
                            {formatBRL(resultado.core.intermediarios.valorAtualLiquido)}
                        </p>
                        <p className="text-calculator-text font-bold">
                            <strong>Alvo a Bater (Líquido Original):</strong>{" "}
                            {formatBRL(resultado.core.intermediarios.valorFinalLiquidoOriginal)}
                        </p>
                    </div>
                </div>
            </details>
        </MainCard>
    );
};
